/**
 * Admin section (008 US3 T018/T019) — customers (the single role-adaptive tab that
 * merges the old Customers + Stores views), activation, entitlements, provider
 * settings, approvals, plus the embedded observability dashboard. Each sub-section
 * is gated by role (gating.ts); the server remains authoritative (006/010).
 *
 * Hosting/CDN WAS "dormant for later": built, tested and mounted nowhere. WS-Z5 resolved that
 * in the open (Direction B R5) — it is the only client of `/api/admin/hosting/settings` and of
 * the R2 credentials the signed-upload broker reads, so no other control covered it, and Rule
 * A turns "no survivor" into a mount rather than a delete. It is the eleventh section,
 * super-admin only.
 */
import { useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import "./admin.css";
import { useSessionStore } from "../stores/session.js";
import { visibleSections, type AdminSection as Section } from "./gating.js";
import { CustomersTab } from "./CustomersTab.js";
import { ActivationTab } from "./ActivationTab.js";
import { EntitlementsTab } from "./EntitlementsTab.js";
import { ProvidersTab } from "./ProvidersTab.js";
import { PromptsTab } from "./PromptsTab.js";
import { ApprovalsTab } from "./ApprovalsTab.js";
import { AppearanceTab } from "./AppearanceTab.js";
import { SegmentationTab } from "./SegmentationTab.js";
import { ImageLibraryTab } from "./ImageLibraryTab.js";
import { WorkspaceDefaultsTab } from "./WorkspaceDefaultsTab.js";
import { HostingTab } from "./HostingTab.js";

/* Exported so the sub-nav's labels have ONE source. `apps/scripts/design-gate.mjs` keeps a
   hand-written copy of this roster per role (`EXPECTED_SUBNAVS`) to hold the running app to it;
   `admin-section.test.tsx` equates that copy with `visibleSections(role)` mapped through this
   record, so the two cannot drift apart unnoticed (review-2 N-3). */
export const SECTION_LABELS: Record<Section, string> = {
  customers: "Customers",
  activation: "Activation & licensing",
  entitlements: "Entitlements",
  providers: "AI providers",
  prompts: "AI prompts",
  approvals: "Template approvals",
  appearance: "Appearance",
  segmenter: "AI matte",
  image_library: "Image library",
  workspace_defaults: "Workspace defaults",
  hosting: "Hosting & CDN",
};

export function AdminSection() {
  const role = useSessionStore((s) => s.role());
  const sections = visibleSections(role);
  /* The section on screen is DERIVED from the live roster, not stored beside it. `active` as
     plain state outlived the role that set it: a session downgraded in place — a refresh that
     comes back `tenant_admin`, an impersonation ending — left `active === "hosting"` and went
     on rendering the R2 credentials form to a role that may not see it, with the button for it
     already gone from the sub-nav. A key that is no longer in `sections` falls back to the
     first section this role MAY see, which is the same rule `visibleSections` filters by. */
  const [chosen, setChosen] = useState<Section | null>(null);
  const active: Section = chosen !== null && sections.includes(chosen) ? chosen : (sections[0] ?? "customers");

  if (sections.length === 0) {
    return (
      <EmptyState
        icon="lock"
        title="Admin access required"
        hint="Ask a workspace administrator to grant you access."
        testId="admin-denied"
      />
    );
  }

  return (
    <section className="admin" data-testid="admin-section">
      <header className="panel-head">
        <div>
          <h1 className="panel-title">Admin</h1>
        </div>
      </header>
      {/* Rule 11 (R3). These ten used to be a `role="tablist"` carrying BOTH tab-bar class
          families at once — `className="admin__nav workspace__tabs"` — and a ten-item
          horizontal bar that scrolls is a list pretending to be a tab bar, with half of it
          off-screen. They are a 200px left list now: a real `<nav>` of `.subnav__item`
          buttons whose current item takes rule 4's tint. Each keeps its `admin-tab-{key}`
          testid, so every probe selector and every suite that names a section still lands. */}
      <div className="admin__layout">
        <nav className="subnav" aria-label="Admin sections" data-testid="admin-subnav">
          {sections.map((s) => (
            /* `aria-current="page"`, not `aria-pressed` (fix round 1, L-1). This is a `<nav>`
               of destinations and `aria-current` is its idiom; `aria-pressed` says "toggle",
               which is what a segmented control is. `undefined` rather than `"false"` on the
               rest: some screen readers announce `aria-current="false"`, and "not the current
               page" is not an announcement. Rule 4's tint is unchanged — `components.css`
               matches BOTH attributes, and the `aria-pressed` selector stays so the rule still
               bites if the attribute comes back. */
            <button
              key={s}
              type="button"
              className="subnav__item"
              aria-current={active === s ? "page" : undefined}
              onClick={() => setChosen(s)}
              data-testid={`admin-tab-${s}`}
            >
              {SECTION_LABELS[s]}
            </button>
          ))}
        </nav>
        <div className="admin__body" data-testid="admin-body">
          {active === "customers" && <CustomersTab />}
          {active === "activation" && <ActivationTab />}
          {active === "entitlements" && <EntitlementsTab />}
          {active === "providers" && <ProvidersTab />}
          {active === "prompts" && <PromptsTab />}
          {active === "approvals" && <ApprovalsTab />}
          {active === "appearance" && <AppearanceTab />}
          {active === "segmenter" && <SegmentationTab />}
          {active === "image_library" && <ImageLibraryTab />}
          {active === "workspace_defaults" && <WorkspaceDefaultsTab />}
          {active === "hosting" && <HostingTab />}
        </div>
      </div>
    </section>
  );
}
